import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { GetSingleBlogsThunk } from "../../redux/states/blogs/thunk";
import {
  ArrowLeft,
  FileText,
  Tag,
  Layers,
  Image as ImageIcon,
  Calendar,
  Hash,
  Eye,
  User,
  FolderOpen,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Share2,
  Clock,
  BookOpen,
} from "lucide-react";

const ViewBlogs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleBlog, loading } = useSelector((state) => state.blogs);
    const API_URL = process.env.REACT_APP_API_URL


  useEffect(() => {
    dispatch(GetSingleBlogsThunk(uuid));
  }, [dispatch, uuid]);

  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-blue-600">
              Loading blog details...
            </p>
            <p className="text-gray-500 mt-2">Please wait</p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  if (!singleBlog) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
          <div className="text-center bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Blog not found
            </h2>
            <p className="text-gray-600 mb-6">
              The blog you are looking for is not available
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto transition-all duration-300 transform hover:scale-105"
            >
              <ArrowLeft className="w-4 h-4" />
              Go back to Blog List
            </button>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return {
          color: "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
        };
      case "inactive":
        return {
          color: "bg-red-100 text-red-800 border-red-200",
          icon: XCircle,
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800 border-gray-200",
          icon: AlertCircle,
        };
    }
  };

  const StatusBadge = getStatusBadge(singleBlog.status);
  const StatusIcon = StatusBadge.icon;

  const createMarkup = (htmlContent) => {
    return { __html: htmlContent || "No content available" };
  };


  const renderJsonSchema = (schema) => {
  try {
    return JSON.stringify(JSON.parse(schema), null, 2);
  } catch (err) {
    return schema || "Invalid JSON";
  }
};


  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-12xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-4 lg:mb-0">
                <div className="flex items-center gap-3 mb-3">
                  <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h1 className="text-sm font-bold text-gray-800 line-clamp-2">
                    {singleBlog.name}
                  </h1>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold border flex items-center gap-1 ${StatusBadge.color}`}
                  >
                    <StatusIcon className="w-4 h-4" />
                    {singleBlog.status}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-gray-600">
                  <span className="flex items-center gap-1">
                    <Hash className="w-4 h-4" />
                    ID: {singleBlog.id}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    Views: {singleBlog.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Created:{" "}
                    {new Date(singleBlog.created_at).toLocaleDateString(
                      "en-IN"
                    )}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Featured Image */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                {singleBlog.image ? (
                  <img
                    src={`${API_URL}/${singleBlog.image}`}
                    alt={singleBlog.name}
                    className="w-full h-100 object-cover"
                  />
                ) : (
                  <div className="w-full h-64 flex flex-col items-center justify-center bg-gray-100 text-gray-400">
                    <ImageIcon className="w-16 h-16 mb-2" />
                    <p>No Featured Image</p>
                  </div>
                )}
              </div>

              {/* Blog Content */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                {/* Meta Information */}
                <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-center gap-2 text-gray-600">
                    <User className="w-4 h-4" />
                    <span>User ID: {singleBlog.user_id}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <FolderOpen className="w-4 h-4" />
                    <span>
                      Category: {singleBlog.category_id_data || "N/A"} (ID:{" "}
                      {singleBlog.category_id})
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>
                      Updated:{" "}
                      {singleBlog.updated_at
                        ? new Date(singleBlog.updated_at).toLocaleDateString(
                            "en-IN"
                          )
                        : "Never"}
                    </span>
                  </div>
                </div>

                {/* Short Description */}
                {singleBlog.short_description && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      Short Description
                    </h2>
                    <div
                      className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={createMarkup(
                        singleBlog.short_description
                      )}
                    />
                  </div>
                )}

                {/* Long Description */}
                {singleBlog.long_description && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Layers className="w-5 h-5 text-green-600" />
                      Long Description
                    </h2>
                    <div
                      className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={createMarkup(
                        singleBlog.long_description
                      )}
                    />
                  </div>
                )}

                {/* Main Description */}
                {singleBlog.description && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                      Main Content
                    </h2>
                    <div
                      className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={createMarkup(
                        singleBlog.description
                      )}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Quick Stats */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Quick Stats
                </h2>
                <div className="space-y-3">
                  <StatCard
                    label="Views"
                    value={singleBlog.views}
                    color="blue"
                    icon={Eye}
                  />
                  <StatCard
                    value={singleBlog?.category_id_data}
                    color="purple"
                    icon={FolderOpen}
                  />
                </div>
              </div>

              {/* SEO Information */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-green-600" />
                  SEO Information
                </h2>
                <div className="space-y-4">
                   <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Title
                    </label>
                  <p className="text-sm text-gray-600">
                    {singleBlog.meta_title || "No keywords available"}
                  </p>
                  </div>
                        <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Description
                    </label>
                  <p className="text-sm text-gray-600">
                    {singleBlog.meta_description || "No keywords available"}
                  </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Keywords
                    </label>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-sm text-gray-600">
                        {singleBlog.meta_keyword || "No keywords available"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Technical Details */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Hash className="w-5 h-5 text-gray-600" />
                  Technical Details
                </h2>
                <div className="space-y-3">
                  <InfoCard
                    label="Slug"
                    value={singleBlog.slug}
                    copyable={true}
                  />
                  <InfoCard
                    label="UUID"
                    value={singleBlog.uuid}
                    copyable={true}
                  />
                  <InfoCard
                    label="Sort By"
                    value={singleBlog.sort_by || "N/A"}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-sm text-gray-600">
                        {singleBlog.tags || "No tags available"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dates Information */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-600" />
                  Date Information
                </h2>
                <div className="space-y-3">
                  <InfoCard
                    label="Created Date"
                    value={new Date(singleBlog.created_at).toLocaleString(
                      "en-IN"
                    )}
                  />
                  <InfoCard
                    label="Updated Date"
                    value={
                      singleBlog.updated_at
                        ? new Date(singleBlog.updated_at).toLocaleString(
                            "en-IN"
                          )
                        : "Never Updated"
                    }
                  />
                </div>
              </div>

              {/* JSON Schema */}
      {singleBlog.meta_schema && (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
    <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
      <FileText className="w-5 h-5 text-purple-600" />
      JSON Schema
    </h2>
    <div className="bg-gray-900 rounded-lg p-4 max-h-48 overflow-y-auto">
      <pre className="text-xs text-green-400 whitespace-pre-wrap">
        {renderJsonSchema(singleBlog.meta_schema)}
      </pre>
    </div>
  </div>
)}

            </div>
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

/* -------------------- Helper Components -------------------- */
const InfoCard = ({ label, value, copyable = false, truncate = false }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    alert("Copied!");
  };

  return (
    <div className="border rounded-xl p-3 bg-white hover:shadow-md transition-shadow duration-300">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-700 mb-1">{label}</p>
          <p
            className={`text-gray-900 font-medium ${
              truncate ? "truncate" : "break-words"
            }`}
          >
            {value || "—"}
          </p>
        </div>
        {copyable && value && (
          <button
            onClick={handleCopy}
            className="ml-2 p-1 text-gray-400 hover:text-blue-600 transition-colors"
            title="Copy"
          >
            <FileText className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, icon: Icon }) => {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    green: "bg-green-50 border-green-200 text-green-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
  };

  return (
    <div
      className={`flex justify-between items-center p-3 rounded-lg border ${colorClasses[color]}`}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="font-medium">{label}</span>
      </div>
      <span className="text-xl font-bold">{value}</span>
    </div>
  );
};

export default ViewBlogs;
